import React, { createContext, useState, useEffect, useRef, useCallback } from 'react';
import api from '../api/axiosConfig';
import { useAuth } from "./AuthContext";

export const SettingsContext = createContext();

const DEFAULT_LANGUAGES = [
  { code: 'en', name: 'English (US)', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
];

export const SettingsProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem('briq_theme') || 'light';
    } catch {
      return 'light';
    }
  });
  const [font, setFont] = useState('Inter');
  const [language, setLanguage] = useState('en');
  const [currencyId, setCurrencyId] = useState(1);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const [activeModules, setActiveModules] = useState({
    crm: true,
    projects: true,
    tasks: true,
    accounting: true,
    materials: true,
    workforce: true,
    vendor: true,
    employee: true,
    inventory: true,
    warehouse: true,
    assets: true,
    documents: true,
    compliance: true,
    reporting: true,
  });

  const [availableLanguages, setAvailableLanguages] = useState(DEFAULT_LANGUAGES);

  // ─── Debounce refs ────────────────────────────────────────────────────────
  // saveTimerRef  : holds the debounce timeout ID for the HTTP POST
  // pendingSaveRef: accumulates all settings changes made within the debounce window
  //                 so they are flushed in ONE single API call instead of many.
  const saveTimerRef = useRef(null);
  const pendingSaveRef = useRef({});

  // ─── 1. LOAD SETTINGS ────────────────────────────────────────────────────
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) { setLoading(false); return; }

        const res = await api.get('/settings');

        if (res.data) {
          // If the user already set a theme in localStorage, keep it and sync to server
          const localTheme = localStorage.getItem('briq_theme');
          if (localTheme) {
            setTheme(localTheme);
            if (res.data.theme !== localTheme) {
              api.post('/settings', { theme: localTheme }).catch(() => {});
            }
          } else if (res.data.theme) {
            setTheme(res.data.theme);
            try {
              localStorage.setItem('briq_theme', res.data.theme);
            } catch {}
          }

          if (res.data.font) setFont(res.data.font);
          if (res.data.language) setLanguage(res.data.language);
          if (res.data.currency_id) setCurrencyId(res.data.currency_id);

          if (res.data.active_modules) {
            try {
              const parsed = typeof res.data.active_modules === 'string'
                ? JSON.parse(res.data.active_modules)
                : res.data.active_modules;
              setActiveModules(prev => ({ ...prev, ...parsed }));
            } catch (e) {
              console.error('Could not parse active_modules', e);
            }
          }

          if (res.data.available_languages) {
            try {
              const parsed = typeof res.data.available_languages === 'string'
                ? JSON.parse(res.data.available_languages)
                : res.data.available_languages;
              if (Array.isArray(parsed) && parsed.length > 0) {
                setAvailableLanguages(parsed);
              }
            } catch (e) {
              console.error('Could not parse available_languages', e);
            }
          }
        }
      } catch (error) {
        console.warn('Using defaults:', error.message);
      } finally {
        setLoading(false);
      }
    };
    
    if (user && user.id) {
        fetchSettings();
    } else {
        setLoading(false);
    }
  }, [user]);

  // ─── 2. CSS FONT INJECTION ────────────────────────────────────────────────
  useEffect(() => {
    if (!font) return;
    const styleId = 'dynamic-font-style';
    let styleTag = document.getElementById(styleId);
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = styleId;
      document.head.appendChild(styleTag);
    }
    styleTag.innerHTML = `
      body, h1, h2, h3, h4, h5, h6, p, span, div, a,
      button, input, select, textarea, li {
        font-family: '${font}', sans-serif !important;
      }
    `;
  }, [font]);

  // ─── 3. APPLY THEME ───────────────────────────────────────────────────────
  useEffect(() => {
    const applyTheme = () => {
      const p = (window.location.pathname || '').toLowerCase();
      if (p === '/login' || p === '/signup' || p.indexOf('/login') !== -1 || p.indexOf('/signup') !== -1) {
        document.documentElement.classList.remove('dark');
        return;
      }
      const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
      if (isDark) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    };

    applyTheme();

    try {
      localStorage.setItem('briq_theme', theme);
    } catch {}

    if (theme === 'system' && window.matchMedia) {
      const media = window.matchMedia('(prefers-color-scheme: dark)');
      media.addEventListener('change', applyTheme);
      return () => media.removeEventListener('change', applyTheme);
    }
  }, [theme]);

  // ─── 4. FLUSH PENDING SAVE TO API ────────────────────────────────────────
  // This is only called by the debounce timer — never directly.
  const flushSave = useCallback(async (payload) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      await api.post('/settings', payload);
    } catch (error) {
      console.error('Save failed:', error);
    }
  }, []);

  // ─── 5. SAVE SETTINGS (DEBOUNCED + BATCHED) ──────────────────────────────
  //
  // HOW IT WORKS:
  //   • State updates happen instantly so the UI is always responsive.
  //   • The actual HTTP POST is debounced by 800 ms.
  //   • If saveSettings is called multiple times within that window (e.g. user
  //     clicks through several languages quickly), all changes are merged into
  //     pendingSaveRef and sent in ONE single API call.
  //   • This eliminates the flood of requests seen in the logs.
  //
  const saveSettings = useCallback((newSettings) => {
    // --- Update React state immediately (UI stays snappy) ---
    if (newSettings.theme !== undefined) {
      setTheme(newSettings.theme);
      try {
        localStorage.setItem('briq_theme', newSettings.theme);
      } catch {}
    }
    if (newSettings.font !== undefined) setFont(newSettings.font);
    if (newSettings.language !== undefined) setLanguage(newSettings.language);
    if (newSettings.currencyId !== undefined) setCurrencyId(newSettings.currencyId);
    if (newSettings.activeModules !== undefined) setActiveModules(newSettings.activeModules);
    if (newSettings.availableLanguages !== undefined) setAvailableLanguages(newSettings.availableLanguages);

    // --- Accumulate into pending batch ---
    pendingSaveRef.current = { ...pendingSaveRef.current, ...newSettings };

    // --- Reset the debounce timer ---
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

    saveTimerRef.current = setTimeout(() => {
      const pending = pendingSaveRef.current;
      pendingSaveRef.current = {}; // Clear immediately to avoid double-sends

      // Build the DB payload — stringify complex objects for storage
      const payload = {
        ...pending,
        active_modules: pending.activeModules
          ? JSON.stringify(pending.activeModules)
          : undefined,
        available_languages: pending.availableLanguages
          ? JSON.stringify(pending.availableLanguages)
          : undefined,
      };

      // Remove undefined keys to keep the payload clean
      Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);

      flushSave(payload);
    }, 800); // ← Wait 800 ms after the LAST change before sending
  }, [flushSave]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  return (
    <SettingsContext.Provider value={{
      theme, font, language, currencyId, loading,
      activeModules,
      availableLanguages,
      saveSettings,
    }}>
      {children}
    </SettingsContext.Provider>
  );
};